export function getApiStatus(err) {
  return err?.response?.status;
}

export function getApiErrorMessage(err, fallback = "Request failed") {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    fallback
  );
}

/**
 * | Status | Meaning           | UI Action                |
| ------ | ----------------- | ------------------------ |
| 401    | Token expired     | Refresh → login if fails |
| 403    | Permission denied | Show snackbar message    |
| 409    | Conflict          | Show backend message     |
| 400    | Validation        | Show field errors        |
 * 
 * Handle common API errors consistently.
 * Returns { handled: boolean, message: string, status?: number }
 *
 * handled=true  -> caller should NOT throw (swallow)
 * handled=false -> caller may throw
 */
export function handleApiError(err, { onAuthError, showSnackBar } = {}) {
  const status = getApiStatus(err);
  const message = getApiErrorMessage(err);

  // Network / no response
  if (!err?.response) {
    showSnackBar?.("Network", "error", "Network error. Please check your connection.");
    return { handled: true, status, message };
  }

  // Auth/session
  if (status === 401 || status === 403) {
    if (status === 401) {
      showSnackBar?.("Session", "error", message || "Session expired. Please sign in again.");
    } else if (status === 403) {
      showSnackBar?.("Authorization", "error", message || "Not authorized to perform this action.");
    }
    onAuthError?.();
    return { handled: true, status, message };
  }

  // Conflict (duplicate)
  if (status === 409) {
    showSnackBar?.("Conflict", "error", message);
    return { handled: true, status, message };
  }

  // Validation / bad request
  if (status === 400 || status === 422) {
    showSnackBar?.("Validation", "error", message);
    return { handled: true, status, message };
  }

  // Not found
  if (status === 404) {
    showSnackBar?.("Not Found", "error", message);
    return { handled: true, status, message };
  }

  // Too many requests
  if (status === 429) {
    showSnackBar?.("Rate limit", "error", message || "Too many requests. Please try again.");
    return { handled: true, status, message };
  }

  // Server errors: show message but allow caller to throw if they want
  if (status >= 500) {
    showSnackBar?.("Server", "error", message || "Server error. Please try again.");
    return { handled: true, status, message };
  }

  // Default: show message and treat as handled (prevents Uncaught promise)
  showSnackBar?.("Error", "error", message);
  return { handled: true, status, message };
}
